import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import type { NewcoreIcon } from '@newcore/icons';
import { Sizes } from '../../enums/sizes';
import { useSlidingPill } from '../../hooks/use-sliding-pill';
import { cn } from '../../utils/cn';
import {
  SegmentedControlButton,
  type SegmentedControlSize,
} from './segmented-control-button';

const PILL_SHADOW = 'shadow-[var(--shadow-xs)]';

export interface SegmentedControlOption<T extends string> {
  value: T;
  /** Segment content; the tooltip and accessible name when `icon` is set. */
  label: ReactNode;
  /** When set, the segment renders the icon instead of the label. */
  icon?: NewcoreIcon;
  /** When set, renders a small count chip after the label. */
  count?: number;
}

type SegmentedControlElementProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'onChange' | 'role'
>;

type SegmentedControlProps<T extends string> = SegmentedControlElementProps & {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Icon-only segments: `MD` matches the default `Input` height. */
  size?: SegmentedControlSize;
};

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
  size = Sizes.SM,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const activeIndex = options.findIndex(o => o.value === value);
  const { containerRef, registerItem, pill } =
    useSlidingPill<HTMLButtonElement>(activeIndex);

  return (
    <div
      {...rest}
      ref={containerRef}
      role='radiogroup'
      aria-disabled={disabled || undefined}
      className={cn(
        'relative inline-flex items-center rounded-lg bg-tertiary p-0.5',
        disabled && 'opacity-50',
        className
      )}
    >
      {pill && (
        <div
          aria-hidden
          className={cn(
            'absolute inset-y-0.5 rounded-md border border-secondary bg-primary transition-[left,width] duration-200 ease-out',
            PILL_SHADOW
          )}
          style={{ left: pill.left, width: pill.width }}
        />
      )}
      {options.map((option, index) => (
        <SegmentedControlButton
          key={option.value}
          ref={registerItem(index)}
          value={option.value}
          label={option.label}
          icon={option.icon}
          count={option.count}
          size={size}
          isActive={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}
